/**
 * PATH: apps/quote/src/pages/pricelist-page/XeroTab.tsx
 * PURPOSE: Xero sync panel for items and contacts
 */

import { RefreshCw, Package, Users, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QueryClient } from '@tanstack/react-query';

interface XeroTabProps {
  queryClient: QueryClient;
  xeroStatus: any;
  xeroStatusError: boolean;
  xeroStatusErrorData: unknown;
  xeroContactsStatus: any;
  xeroSyncMutation: any;
  xeroContactsSyncMutation: any;
}

export default function XeroTab({
  queryClient,
  xeroStatus,
  xeroStatusError,
  xeroStatusErrorData,
  xeroContactsStatus,
  xeroSyncMutation,
  xeroContactsSyncMutation,
}: XeroTabProps) {
  return (
    <div className="space-y-6">
      {/* Error State */}
      {xeroStatusError && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-200">Unable to check Xero connection status</h3>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                {xeroStatusErrorData instanceof Error ? xeroStatusErrorData.message : 'Connection check failed. This may be a temporary issue.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['xero-sync-status'] })}
            className="mt-3 px-4 py-2 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-md hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
        </div>
      )}

      {/* Connection Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Xero Items Sync */}
        <SyncCard
          icon={Package}
          title="Product Items"
          subtitle="Sync products from Xero inventory"
          isConnected={xeroStatus?.is_connected}
          stats={[
            { label: 'Items synced', value: xeroStatus?.xero_items_in_pricebook || 0 },
            { label: 'Last sync', value: xeroStatus?.last_sync ? new Date(xeroStatus.last_sync).toLocaleDateString() : 'Never' },
            ...(xeroStatus?.pending_price_suggestions ? [{ label: 'Pending suggestions', value: xeroStatus.pending_price_suggestions, highlight: true }] : []),
          ]}
          onSync={() => xeroSyncMutation.mutate()}
          isSyncing={xeroSyncMutation.isPending}
          buttonColor="bg-blue-600 hover:bg-blue-700"
          buttonLabel="Sync Items"
        />

        {/* Xero Contacts Sync */}
        <SyncCard
          icon={Users}
          title="Supplier Contacts"
          subtitle="Sync suppliers from Xero contacts"
          isConnected={xeroContactsStatus?.is_connected}
          stats={[
            { label: 'From Xero', value: xeroContactsStatus?.xero_suppliers_count || 0 },
            { label: 'Total suppliers', value: xeroContactsStatus?.total_suppliers || 0 },
            { label: 'Last sync', value: xeroContactsStatus?.last_sync ? new Date(xeroContactsStatus.last_sync).toLocaleDateString() : 'Never' },
          ]}
          onSync={() => xeroContactsSyncMutation.mutate()}
          isSyncing={xeroContactsSyncMutation.isPending}
          buttonColor="bg-purple-600 hover:bg-purple-700"
          buttonLabel="Sync Contacts"
        />
      </div>

      {/* Not Connected Banner */}
      {!xeroStatus?.is_connected && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-800 dark:text-amber-200">Xero Not Connected</h3>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Connect Xero to sync products and suppliers automatically.
              </p>
            </div>
            <a
              href="https://dashboard.quotech.ai/settings/integrations"
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
            >
              Connect Xero
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function SyncCard({
  icon: Icon,
  title,
  subtitle,
  isConnected,
  stats,
  onSync,
  isSyncing,
  buttonColor,
  buttonLabel,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  isConnected?: boolean;
  stats: Array<{ label: string; value: string | number; highlight?: boolean }>;
  onSync: () => void;
  isSyncing: boolean;
  buttonColor: string;
  buttonLabel: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn("h-12 w-12 rounded-lg flex items-center justify-center", isConnected ? "bg-green-100" : "bg-gray-100")}>
            <Icon className={cn("h-6 w-6", isConnected ? "text-green-600" : "text-gray-400")} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        {stats.map((stat) => (
          <div key={stat.label} className="flex justify-between text-sm">
            <span className="text-gray-500">{stat.label}</span>
            <span className={cn("font-medium", stat.highlight && "text-amber-600")}>{stat.value}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onSync}
        disabled={isSyncing || !isConnected}
        className={cn(
          "w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg",
          isConnected ? `${buttonColor} text-white` : "bg-gray-100 text-gray-400 cursor-not-allowed"
        )}
      >
        <RefreshCw className={cn("h-4 w-4", isSyncing && "animate-spin")} />
        {isSyncing ? 'Syncing...' : buttonLabel}
      </button>
    </div>
  );
}
